import PropTypes from 'prop-types';
import React from 'react';

import Alert from '@mui/material/Alert';
import { QRCodeSVG } from 'qrcode.react';

import auth from '../../../services/appAuth';

const STEP_CHOOSE = 'choose';
const STEP_PHONE = 'phone';
const STEP_TOTP = 'totp';
const STEP_CODE = 'code';

// SMS enrollment costs money per message and is not available in every
// environment (stage has no SMS region enabled) — gate it per deployment.
// Sign-in with an already-enrolled SMS factor is intentionally not gated.
const SMS_ENROLLMENT_ENABLED = process.env.REACT_APP_MFA_SMS === 'true';

function errorText(t, error, viaMethod) {
  switch (error && error.code) {
    case 'auth/invalid-phone-number':
      return t('invalid_phone_number');
    case 'auth/invalid-verification-code':
      return t('invalid_code');
    case 'auth/too-many-requests':
      return t('too_many_attempts_try_later');
    case 'auth/requires-recent-login':
      return t('please_sign_in_again_to_continue');
    case 'auth/operation-not-allowed':
      // Same code for two very different causes: TOTP disabled on the
      // project vs the SMS region policy blocking the phone's region.
      return viaMethod === 'sms'
        ? t('sms_region_not_enabled')
        : t('authenticator_app_not_available');
    default:
      return (error && error.message) || t('invalid_code');
  }
}

export default function MfaEnrollmentWizard(props) {
  const t = props.context.t;
  const [step, setStep] = React.useState(STEP_CHOOSE);
  const [method, setMethod] = React.useState(null);
  const [phone, setPhone] = React.useState(
    (props.details && props.details.phoneNumber) || ''
  );
  const [totpInfo, setTotpInfo] = React.useState(null);
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const fail = (err, viaMethod) => {
    console.error(err);
    setError(errorText(t, err, viaMethod));
    setBusy(false);
    if (err && err.code === 'auth/requires-recent-login') {
      // Firebase only allows enrollment shortly after an actual sign-in, and
      // a session restored from storage is never "recent" — sign out so the
      // next password login re-enters the wizard inside the recency window.
      setTimeout(props.onCancel, 2500);
    }
  };

  const chooseSms = () => {
    setError(null);
    setMethod('sms');
    setStep(STEP_PHONE);
  };

  const chooseTotp = async () => {
    setError(null);
    setBusy(true);
    try {
      const info = await auth.startTotpEnrollment();
      setTotpInfo(info);
      setMethod('totp');
      setStep(STEP_TOTP);
      setBusy(false);
    } catch (err) {
      fail(err, 'totp');
    }
  };

  const sendSms = async () => {
    const normalized = phone.startsWith('+') ? phone : `+${phone}`;
    if (!/^\+[1-9]\d{1,14}$/.test(normalized)) {
      setError(t('invalid_phone_number'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await auth.startPhoneEnrollment(normalized);
      setStep(STEP_CODE);
      setBusy(false);
    } catch (err) {
      fail(err, 'sms');
    }
  };

  const submitCode = async () => {
    setError(null);
    setBusy(true);
    try {
      if (method === 'sms') {
        await auth.finishPhoneEnrollment(code);
      } else {
        await auth.finishTotpEnrollment(code);
      }
      props.onComplete();
    } catch (err) {
      fail(err, method);
    }
  };

  const goBackToChoose = (e) => {
    e.preventDefault();
    setError(null);
    setCode('');
    setStep(STEP_CHOOSE);
  };

  const onEnter = (action) => (e) => {
    if (e.key === 'Enter' && !busy) {
      action();
    }
  };

  const actionButton = (label, onClick) => (
    <div className='form-group form-actions'>
      <div className='col-xs-6 col-xs-offset-3 login-field'>
        <button
          className='login-submit btn btn-success btn-block'
          onClick={onClick}
          disabled={busy}
        >
          {label}
        </button>
      </div>
    </div>
  );

  return (
    <div className='login-input-field mfa-enroll'>
      <h2>{t('multifactor_authentication')}</h2>

      {error && (
        <Alert className='mfa-error' severity='error'>
          {error}
        </Alert>
      )}

      {step === STEP_CHOOSE && (
        <div>
          <p>{t('mfa_setup_intro')}</p>
          <div className='form-group form-actions'>
            <div className='col-xs-6 col-xs-offset-3 login-field'>
              {SMS_ENROLLMENT_ENABLED && (
                <button
                  className='login-submit btn btn-success btn-block'
                  onClick={chooseSms}
                  disabled={busy}
                >
                  {t('mfa_method_sms')}
                </button>
              )}
              <button
                className='login-submit btn btn-success btn-block'
                onClick={chooseTotp}
                disabled={busy}
              >
                {t('mfa_method_totp')}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === STEP_PHONE && (
        <div>
          <p>{t('mfa_confirm_phone')}</p>
          <div className='mfa-field'>
            <label htmlFor='input-mfa-phone'>{t('phone_number')}</label>
            <input
              id='input-mfa-phone'
              type='tel'
              value={phone}
              placeholder='+972501234567'
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={onEnter(sendSms)}
              disabled={busy}
            />
          </div>
          {actionButton(t('send_code'), sendSms)}
        </div>
      )}

      {step === STEP_TOTP && totpInfo && (
        <div>
          <p>{t('mfa_scan_qr')}</p>
          <div className='mfa-qr'>
            <div className='mfa-qr-tile'>
              <QRCodeSVG value={totpInfo.qrUrl} size={150} />
            </div>
            <span className='mfa-secret'>{totpInfo.secretKey}</span>
          </div>
          {actionButton(t('next'), () => {
            setError(null);
            setStep(STEP_CODE);
          })}
        </div>
      )}

      {step === STEP_CODE && (
        <div>
          <p>
            {method === 'sms'
              ? t('mfa_enter_code_from_sms')
              : t('mfa_enter_code_from_app')}
          </p>
          <div className='mfa-field'>
            <label htmlFor='input-mfa-enroll-code'>{t('mfa_code')}</label>
            <input
              id='input-mfa-enroll-code'
              className='mfa-code-input'
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={onEnter(submitCode)}
              disabled={busy}
            />
          </div>
          {actionButton(t('submit'), submitCode)}
        </div>
      )}

      <div className='mfa-links'>
        {step !== STEP_CHOOSE && (
          <a href='#' onClick={goBackToChoose}>
            {t('back')}
          </a>
        )}
        <a
          href='#'
          onClick={(e) => {
            e.preventDefault();
            props.onCancel();
          }}
        >
          {t('cancel')}
        </a>
      </div>
    </div>
  );
}

MfaEnrollmentWizard.propTypes = {
  context: PropTypes.object.isRequired,
  details: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};
